// src/pages/MyMenu.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, getDocs, limit, doc, runTransaction, arrayUnion, increment, getDoc, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import './MyMenu.css';

const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// SVG ICONS FOR MEALS
const BreakfastIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/><path d="M3 8h18"/><path d="M3 3h18v3H3z"/></svg> );
const LunchIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.33-6 4Z"/><path d="M12 12v8"/><path d="M12 12H5a3 3 0 0 0-3 3v2h10v-2a3 3 0 0 0-3-3Z"/></svg> );
const DinnerIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14a6 6 0 0 0-12 0"/><path d="M5 14h14"/><path d="M11 4V2"/><path d="M7 5V3"/><path d="M15 5V3"/><path d="M3 14h18v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Z"/></svg> );

const MyMenu = () => {
    const [user, authLoading] = useAuthState(auth);
    const [subscription, setSubscription] = useState(null);
    const [subscriptionId, setSubscriptionId] = useState(null);
    const [pendingRequest, setPendingRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [weeklyMenu, setWeeklyMenu] = useState(null);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [dayToCancel, setDayToCancel] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [viewMode, setViewMode] = useState('calendar');
    const [activeDay, setActiveDay] = useState(DAY_ORDER[0]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            setLoading(false);
            setError("Please log in to view your menu.");
            return;
        }

        const fetchSubscriptionAndBuildMenu = async () => {
            try {
                setLoading(true);
                const userSubRef = doc(db, 'user_subscriptions', user.uid);
                const userSubSnap = await getDoc(userSubRef);

                if (userSubSnap.exists()) {
                    const subData = userSubSnap.data();

                    if (!subData.cancellationPolicy) {
                        const totalDays = subData.subscriptionDuration || (subData.selectedDates || []).length;
                        if (totalDays >= 30) subData.cancellationPolicy = { allowed: 8, fullRefundCount: 4 };
                        else if (totalDays >= 7) subData.cancellationPolicy = { allowed: 1, fullRefundCount: 1 };
                        else subData.cancellationPolicy = { allowed: 0, fullRefundCount: 0 };
                    }
                    
                    setSubscription(subData);
                    setSubscriptionId(userSubSnap.id);

                    if (subData.selectedCombo && subData.cuisineType) {
                        const mealTypes = ['breakfast', 'lunch', 'dinner'];
                        const packagePromises = mealTypes.map(type => {
                            const pkgInfo = subData.selectedCombo[type];
                            return pkgInfo && pkgInfo.packageId ? getDoc(doc(db, 'packages', pkgInfo.packageId)) : Promise.resolve(null);
                        });
                        
                        const packageSnapshots = await Promise.all(packagePromises);
                        const fetchedMenus = {
                            breakfast: packageSnapshots[0]?.data()?.weeklyMenu || null,
                            lunch: packageSnapshots[1]?.data()?.weeklyMenu || null,
                            dinner: packageSnapshots[2]?.data()?.weeklyMenu || null,
                        };
                        generateDynamicMenu(fetchedMenus);
                    }
                } else {
                    const q = query(
                        collection(db, "package_request"), 
                        where("customerId", "==", user.uid), 
                        where("requestStatus", "==", "pending"),
                        orderBy("requestDate", "desc"),
                        limit(1)
                    );
                    const requestSnapshot = await getDocs(q);

                    if (!requestSnapshot.empty) {
                        setPendingRequest(requestSnapshot.docs[0].data());
                    } else {
                        // This error message is now only set if there is truly nothing.
                        setError("No active subscription found.");
                    }
                }
            } catch (err) {
                console.error("Error fetching subscription data:", err);
                setError(err.message);
                setSubscription(null);
            } finally {
                setLoading(false);
            }
        };

        fetchSubscriptionAndBuildMenu();
    }, [user, authLoading]);
    
    const generateDynamicMenu = (fetchedMenus) => {
        const generated = {};
        DAY_ORDER.forEach((dayName) => {
            generated[dayName] = {
                breakfast: getMealForDay(fetchedMenus.breakfast, dayName),
                lunch: getMealForDay(fetchedMenus.lunch, dayName),
                dinner: getMealForDay(fetchedMenus.dinner, dayName)
            };
        });
        setWeeklyMenu(generated);
    };

    const getMealForDay = (menuArray, dayName) => {
        if (!menuArray || menuArray.length === 0) return 'Not Subscribed';
        const dayMenu = menuArray.find(item => item.dayName === dayName);
        return dayMenu?.meal?.name || 'Not Available';
    };
    
    const isCancellationAllowed = (dateStr) => {
        try {
            const mealDate = new Date(dateStr);
            if (isNaN(mealDate.getTime())) return false;
            mealDate.setHours(0, 0, 0, 0);
            const now = new Date();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (mealDate <= today) return false;
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (mealDate.getTime() === tomorrow.getTime()) {
                return now.getHours() < 19;
            }
            return true;
        } catch (error) {
            console.error("Error in isCancellationAllowed:", error);
            return false;
        }
    };

    const openCancelModal = (dateStr) => {
        const dailyCost = subscription.dailySubtotal;
        const { allowed, fullRefundCount } = subscription.cancellationPolicy || { allowed: 0, fullRefundCount: 0 };
        const used = subscription.cancellationsUsed || 0;
        let refundAmount = 0;
        if (used < fullRefundCount) refundAmount = dailyCost;
        else if (used < allowed) refundAmount = Math.round(dailyCost * 0.5);
        setDayToCancel({ date: dateStr, refundAmount });
        setIsModalOpen(true);
    };

    const handleCancelDay = async () => {
        if (!dayToCancel || isCancelling || !user || !subscriptionId) return;
        setIsCancelling(true);
        try {
            const walletQuery = query(collection(db, "customer_wallets"), where("customerId", "==", user.uid), limit(1));
            const walletSnapshot = await getDocs(walletQuery);
            if (walletSnapshot.empty) throw new Error("Customer wallet not found.");
            const walletRef = walletSnapshot.docs[0].ref;
            const subscriptionRef = doc(db, "user_subscriptions", subscriptionId);

            await runTransaction(db, async (transaction) => {
                const refundTransaction = {
                    amount: dayToCancel.refundAmount, type: 'credit', source: 'cancellation_refund',
                    description: `Refund for meal on ${new Date(dayToCancel.date).toLocaleDateString()}`,
                    timestamp: new Date()
                };
                const dateToCancelISO = new Date(dayToCancel.date).toISOString();
                transaction.update(subscriptionRef, { cancelledDates: arrayUnion(dateToCancelISO), cancellationsUsed: increment(1) });
                transaction.update(walletRef, { walletCoins: increment(dayToCancel.refundAmount), transactions: arrayUnion(refundTransaction) });
            });

            setSubscription(prev => ({ ...prev, cancellationsUsed: (prev.cancellationsUsed || 0) + 1, cancelledDates: [...(prev.cancelledDates || []), new Date(dayToCancel.date).toISOString()] }));
            alert("Meal cancelled successfully! Your wallet has been credited.");
        } catch (err) {
            console.error("Error cancelling subscription day:", err);
            alert("Failed to cancel the meal. Please try again.");
        } finally {
            setIsCancelling(false);
            setIsModalOpen(false);
            setDayToCancel(null);
        }
    };
    
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const days = Array(firstDayOfMonth).fill(null);
        for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
        return days;
    };

    const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    const isDateInSubscription = (date) => {
        if (!subscription) return false;
        const datesArray = subscription.selectedDates || subscription.subscriptionDates || [];
        return datesArray.some(d => new Date(d).toDateString() === date.toDateString());
    };
    const isDateCancelled = (date) => {
        if (!subscription || !subscription.cancelledDates) return false;
        return subscription.cancelledDates.some(d => new Date(d).toDateString() === date.toDateString());
    };

    if (loading || authLoading) { return <div className="status-container">Loading Your Menu...</div>; }
    if (!user) { return ( <div className="no-subscription-container"><h2>My Menu</h2><p>Please log in to view your active subscriptions.</p><Link to="/" className="browse-packages-btn">Go to Homepage</Link></div> ); }
    
    if (pendingRequest) {
        return (
            <div className="my-menu-page">
                 <h1 className="page-title">My Menu</h1>
                 <div className="no-subscription-container">
                    <h2>Your Subscription is Pending Review</h2>
                    <p>Your request for the <strong>"{pendingRequest.packageName}"</strong> plan is currently being processed.</p>
                    <p>You will be notified once it has been approved. Thank you for your patience!</p>
                </div>
            </div>
        );
    }
    
    if (!subscription) { return ( <div className="no-subscription-container"><h2>You have no active subscriptions</h2><p>Browse our delicious and healthy meal packages to start your subscription today!</p><Link to="/package" className="browse-packages-btn">Browse Packages</Link></div> ); }

    const datesArray = subscription.selectedDates || subscription.subscriptionDates || [];
    const { cancellationPolicy = { allowed: 0, fullRefundCount: 0 }, cancellationsUsed = 0 } = subscription;
    const cancellationsRemaining = (cancellationPolicy.allowed || 0) - cancellationsUsed;

    return (
        <div className="my-menu-page">
            <h1 className="page-title">My Menu</h1>
            
            <div className="subscription-details-card">
                <h2>Your Active Plan</h2>
                <div className="details-grid">
                    <div><strong>Package:</strong> {subscription.packageName}</div>
                    <div><strong>Daily Total:</strong> ₹{subscription.dailySubtotal}</div>
                    <div><strong>Dates Count:</strong> {datesArray.length}</div>
                </div>
                <h3>Your Selected Combo:</h3>
                <div className="combo-details">
                    {Object.entries(subscription.selectedCombo || {}).map(([type, pkg]) => pkg && (
                        <div key={type} className="combo-item">
                            <span className="meal-type">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                            <span className="package-name">{pkg.name}</span>
                            <span className="package-price">₹{pkg.price}</span>
                        </div>
                    ))}
                </div>
            </div>
            
            <div className="cancellation-section">
                <div className="section-header">
                    <h2>Manage Your Meals</h2>
                    <div className="view-toggle">
                        <button className={viewMode === 'calendar' ? 'active' : ''} onClick={() => setViewMode('calendar')}>Calendar View</button>
                        <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>List View</button>
                    </div>
                </div>
                <div className="cancellation-info">
                    <p>You have <strong>{cancellationsRemaining}</strong> of <strong>{cancellationPolicy.allowed || 0}</strong> cancellations remaining.</p>
                    <p className="cancellation-note">
                        <strong>Note:</strong> Cancellations are allowed for all future meals, except for tomorrow's meals which can only be cancelled before 7 PM today.
                    </p>
                    {datesArray.length === 0 && <p style={{color: '#ef4444'}}>No upcoming meals scheduled.</p>}
                </div>
                
                {viewMode === 'calendar' && (
                    <div className="calendar-view">
                        <div className="calendar-container">
                            <div className="calendar-header">
                                <button className="calendar-nav" onClick={prevMonth}>&lt;</button>
                                <h4>{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h4>
                                <button className="calendar-nav" onClick={nextMonth}>&gt;</button>
                            </div>
                            <div className="calendar-weekdays">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <span key={day}>{day}</span>)}
                            </div>
                            <div className="calendar-grid">
                                {getDaysInMonth(currentMonth).map((date, index) => {
                                    if (!date) return <div key={`empty-${index}`} className="calendar-day empty"></div>;
                                    const isInSubscription = isDateInSubscription(date);
                                    const isCancelled = isDateCancelled(date);
                                    const canCancel = isInSubscription && !isCancelled && isCancellationAllowed(date.toISOString());
                                    let dayClass = 'calendar-day';
                                    if (isInSubscription) dayClass += ' subscription-day';
                                    if (isCancelled) dayClass += ' cancelled';
                                    if (canCancel) dayClass += ' can-cancel';
                                    return (
                                        <div key={date.toISOString()} className={dayClass} onClick={() => canCancel && openCancelModal(date.toISOString())}>
                                            {date.getDate()}
                                            {isInSubscription && !isCancelled && (
                                                <div className="meal-indicators">
                                                    <span className="breakfast-dot"></span>
                                                    <span className="lunch-dot"></span>
                                                    <span className="dinner-dot"></span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="calendar-legend">
                            <div className="legend-item"><div className="legend-color subscription-day"></div><span>Meal Scheduled</span></div>
                            <div className="legend-item"><div className="legend-color can-cancel"></div><span>Can Cancel</span></div>
                            <div className="legend-item"><div className="legend-color cancelled"></div><span>Cancelled</span></div>
                        </div>
                    </div>
                )}
                
                {viewMode === 'list' && (
                    <div className="upcoming-meals-list">
                        {datesArray
                            .sort((a, b) => new Date(a) - new Date(b))
                            .filter(date => new Date(date) >= new Date(new Date().setHours(0, 0, 0, 0)))
                            .map((dateStr) => {
                                const date = new Date(dateStr);
                                const dayName = DAY_ORDER[date.getDay()];
                                const isCancelled = subscription.cancelledDates && subscription.cancelledDates.some(d => new Date(d).toDateString() === date.toDateString());
                                const canCancel = !isCancelled && isCancellationAllowed(dateStr);
                                return (
                                    <div key={dateStr} className={`meal-date-item ${isCancelled ? 'cancelled' : ''}`}>
                                        <div className="date-info">
                                            <span className="day-of-week">{dayName}</span>
                                            <span className="full-date">{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                        </div>
                                        {isCancelled ? (
                                            <span className="cancelled-tag">Cancelled</span>
                                        ) : (
                                            <button className={`cancel-btn ${canCancel ? '' : 'disabled'}`} onClick={() => canCancel && openCancelModal(dateStr)} disabled={!canCancel}>
                                                {canCancel ? 'Cancel Meal' : 'Cannot Cancel'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>
            
            <div className="weekly-menu-section">
                <h2>Weekly Menu</h2>
                <p>Here's what you'll be enjoying this week:</p>
                <div className="menu-tabs">
                    {DAY_ORDER.map(day => (
                        <button key={day} className={`menu-tab-btn ${activeDay === day ? 'active' : ''}`} onClick={() => setActiveDay(day)}>
                            {day.slice(0, 3)}
                        </button>
                    ))}
                </div>
                <div className="menu-tab-content">
                    {weeklyMenu && weeklyMenu[activeDay] && (
                        <div className="daily-meal-cards">
                            <div className="meal-card">
                                <div className="meal-card-icon breakfast"><BreakfastIcon /></div>
                                <div className="meal-card-details">
                                    <div className="meal-card-type">Breakfast</div>
                                    <h4 className="meal-card-name">{weeklyMenu[activeDay].breakfast}</h4>
                                </div>
                            </div>
                            <div className="meal-card">
                                <div className="meal-card-icon lunch"><LunchIcon /></div>
                                <div className="meal-card-details">
                                    <div className="meal-card-type">Lunch</div>
                                    <h4 className="meal-card-name">{weeklyMenu[activeDay].lunch}</h4>
                                </div>
                            </div>
                            <div className="meal-card">
                                <div className="meal-card-icon dinner"><DinnerIcon /></div>
                                <div className="meal-card-details">
                                    <div className="meal-card-type">Dinner</div>
                                    <h4 className="meal-card-name">{weeklyMenu[activeDay].dinner}</h4>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {isModalOpen && (
                <div className="modal-overlay" onClick={() => !isCancelling && setIsModalOpen(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <h3>Cancel Meal</h3>
                        <p>Are you sure you want to cancel your meal on {dayToCancel && new Date(dayToCancel.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}?</p>
                        {dayToCancel && dayToCancel.refundAmount > 0 ? (
                            <p>You will receive a refund of ₹{dayToCancel.refundAmount} to your wallet.</p>
                        ) : (
                            <p>No refund will be issued for this cancellation.</p>
                        )}
                        <div className="modal-actions">
                            <button className="modal-btn secondary" onClick={() => setIsModalOpen(false)} disabled={isCancelling}>
                                Keep Meal
                            </button>
                            <button className="modal-btn primary" onClick={handleCancelDay} disabled={isCancelling}>
                                {isCancelling ? 'Processing...' : 'Cancel Meal'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyMenu;